<template>
  <div class="page-container animate-fade-in-up">
    <PageHeader icon="el-icon-collection" title="行为库管理" subtitle="管理离职风险评估的行为指标库，支持分类管理和行为项配置" />

    <!-- 工具栏 -->
    <div class="toolbar">
      <el-input v-model="search" placeholder="搜索行为描述关键词" prefix-icon="el-icon-search" clearable style="width: 260px;" />
      <el-select v-model="filterCategories" placeholder="全部分类" clearable multiple collapse-tags style="width: 180px; margin-left: 12px;">
        <el-option v-for="c in categories" :key="c.category_id" :label="c.category_name" :value="c.category_id" />
      </el-select>
      <div style="margin-left: auto; display: flex; gap: 12px;">
        <el-button :type="batchMode ? 'primary' : ''" icon="el-icon-s-operation" class="btn-click" @click="toggleBatchMode">
          {{ batchMode ? '完成批量' : '批量操作' }}
        </el-button>
        <el-button icon="el-icon-s-grid" class="btn-click" @click="categoryDialogVisible = true">分类管理</el-button>
        <el-button icon="el-icon-download" class="btn-click" @click="downloadTemplate">下载模板</el-button>
        <el-button v-if="$hasPermission('behavior:create')" icon="el-icon-upload2" class="btn-click" @click="openImport">Excel导入</el-button>
        <el-button v-if="$hasPermission('behavior:create')" type="primary" icon="el-icon-plus" class="btn-click" @click="openBehaviorDialog()">新增行为项</el-button>
      </div>
    </div>

    <!-- 批量操作栏（表格上方） -->
    <div v-if="batchMode" class="batch-bar-top">
      <span v-if="selectedRows.length" class="batch-info">已选择 {{ selectedRows.length }} 项</span>
      <span v-else class="batch-info" style="color: var(--color-text-muted);">请勾选要操作的行为项</span>
      <div class="batch-actions">
        <el-button size="small" icon="el-icon-check" :disabled="!selectedRows.length" @click="batchEnable">批量启用</el-button>
        <el-button size="small" icon="el-icon-close" :disabled="!selectedRows.length" @click="batchDisable">批量禁用</el-button>
        <el-dropdown size="small" :disabled="!selectedRows.length" @command="batchChangeCategory">
          <el-button size="small">
            批量修改分类<i class="el-icon-arrow-down el-icon--right"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item v-for="c in categories" :key="c.category_id" :command="c.category_id">
              {{ c.category_name }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        <el-button size="small" @click="clearSelection">清空选择</el-button>
      </div>
    </div>

    <!-- 表格区域 -->
    <div class="table-wrapper">
      <el-table
        ref="tableRef"
        :data="pagedBehaviors"
        class="data-table"
        stripe
        :height="tableHeight"
        @selection-change="handleSelectionChange"
      >
        <el-table-column v-if="batchMode" type="selection" width="45" />
        <el-table-column label="分类" width="110">
          <template slot-scope="scope">
            <span class="category-tag">{{ categoryNameOf(scope.row.category_id) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="description" label="行为描述" min-width="240" />
        <el-table-column prop="score" label="分值" width="70">
          <template slot-scope="scope">
            +{{ scope.row.score }}
          </template>
        </el-table-column>
        <el-table-column prop="evidence_count" label="案例数" width="80" />
        <el-table-column prop="correlation_rate" label="相关性" width="85">
          <template slot-scope="scope">
            {{ scope.row.correlation_rate }}%
          </template>
        </el-table-column>
        <el-table-column label="自动获取" width="85">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.auto_fetch_enabled === 1" size="mini" type="success">是</el-tag>
            <el-tag v-else size="mini" type="info">否</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="80">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'info'" size="small">
              {{ scope.row.status === 1 ? '生效' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="120">
          <template slot-scope="scope">
            <el-button type="text" class="action-edit" @click="openViewDialog(scope.row)">查看</el-button>
            <el-button v-if="$hasPermission('behavior:edit')" type="text" class="action-edit" @click="openBehaviorDialog(scope.row)">编辑</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-bar">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :total="filteredBehaviors.length"
          :page-size="pageSize"
          :current-page="page"
          :page-sizes="[10, 20, 50, 100]"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <!-- 分类管理弹窗 -->
    <el-dialog title="分类管理" :visible.sync="categoryDialogVisible" width="560px" :append-to-body="true">
      <el-table :data="categories" size="small" class="category-manage-table">
        <el-table-column label="排序" width="60">
          <template slot-scope="scope">
            {{ scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column prop="category_name" label="分类名称" min-width="120">
          <template slot-scope="scope">
            <el-input v-if="scope.row._editing" v-model="scope.row._editName" size="mini" />
            <span v-else>{{ scope.row.category_name }}</span>
          </template>
        </el-table-column>
        <el-table-column label="行为项数" width="90">
          <template slot-scope="scope">
            {{ behaviorCountOf(scope.row.category_id) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180">
          <template slot-scope="scope">
            <template v-if="scope.row._editing">
              <el-button type="text" @click="saveCategoryEdit(scope.row)">保存</el-button>
              <el-button type="text" @click="cancelCategoryEdit(scope.row)">取消</el-button>
            </template>
            <template v-else>
              <el-button type="text" :disabled="scope.$index === 0" @click="moveCategory(scope.$index, -1)">上移</el-button>
              <el-button type="text" :disabled="scope.$index === categories.length - 1" @click="moveCategory(scope.$index, 1)">下移</el-button>
              <el-button type="text" @click="startCategoryEdit(scope.row)">编辑</el-button>
              <el-button type="text" style="color: var(--color-high);" @click="deleteCategory(scope.row)">删除</el-button>
            </template>
          </template>
        </el-table-column>
      </el-table>
      <div style="margin-top: 16px;">
        <el-button v-if="!addingCategory" type="primary" size="small" icon="el-icon-plus" @click="addingCategory = true">新增分类</el-button>
        <div v-else style="display: flex; gap: 8px;">
          <el-input v-model="newCategoryName" size="small" placeholder="输入分类名称" style="width: 200px;" @keyup.enter.native="confirmAddCategory" />
          <el-button size="small" type="primary" @click="confirmAddCategory">确认</el-button>
          <el-button size="small" @click="addingCategory = false">取消</el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 查看行为项弹窗（只读） -->
    <el-dialog title="行为项详情" :visible.sync="viewDialogVisible" width="560px" :append-to-body="true">
      <div v-if="viewRow" class="view-dialog-body">
        <div class="view-row"><span class="view-label">所属分类：</span><span>{{ categoryNameOf(viewRow.category_id) }}</span></div>
        <div class="view-row"><span class="view-label">行为描述：</span><p class="view-text">{{ viewRow.description }}</p></div>
        <div class="view-row"><span class="view-label">分值：</span><span class="score-cell">+{{ viewRow.score }}</span></div>
        <div class="view-row"><span class="view-label">案例数：</span><span>{{ viewRow.evidence_count }}</span></div>
        <div class="view-row"><span class="view-label">相关性：</span><span class="correlation-cell">{{ viewRow.correlation_rate }}%</span></div>
        <div class="view-row"><span class="view-label">历史依据：</span><p class="view-text">{{ viewRow.evidence_desc || '无' }}</p></div>
        <div class="view-row"><span class="view-label">不了解提示：</span><p class="view-text">{{ viewRow.hint_info || '无' }}</p></div>
        <div class="view-row"><span class="view-label">自动获取：</span><el-tag :type="viewRow.auto_fetch_enabled === 1 ? 'success' : 'info'" size="small">{{ viewRow.auto_fetch_enabled === 1 ? '是' : '否' }}</el-tag></div>
        <div class="view-row"><span class="view-label">状态：</span><el-tag :type="viewRow.status === 1 ? 'success' : 'info'" size="small">{{ viewRow.status === 1 ? '生效' : '禁用' }}</el-tag></div>
      </div>
      <span slot="footer">
        <el-button @click="viewDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 新增/编辑行为项弹窗 -->
    <el-dialog :title="behaviorDialog.isEdit ? '编辑行为项' : '新增行为项'" :visible.sync="behaviorDialog.visible" width="560px" :append-to-body="true">
      <el-form :model="behaviorDialog.form" label-width="110px">
        <el-form-item label="所属分类" required>
          <el-select v-model="behaviorDialog.form.category_id" placeholder="选择分类" style="width: 100%;">
            <el-option v-for="cat in categories" :key="cat.category_id" :label="cat.category_name" :value="cat.category_id" />
          </el-select>
        </el-form-item>
        <el-form-item label="行为描述" required>
          <el-input type="textarea" v-model="behaviorDialog.form.description" :rows="3" placeholder="描述该行为的具体表现" />
        </el-form-item>
        <el-form-item label="分值" required>
          <el-input-number v-model="behaviorDialog.form.score" :min="1" :max="30" />
        </el-form-item>
        <el-form-item label="案例数">
          <el-input-number v-model="behaviorDialog.form.evidence_count" :min="0" />
        </el-form-item>
        <el-form-item label="相关性 %">
          <el-input-number v-model="behaviorDialog.form.correlation_rate" :min="0" :max="100" />
        </el-form-item>
        <el-form-item label="历史依据">
          <el-input type="textarea" v-model="behaviorDialog.form.evidence_desc" :rows="2" placeholder="历史依据说明，展示在评估页 tooltip 中" />
        </el-form-item>
        <el-form-item label="不了解提示">
          <el-input type="textarea" v-model="behaviorDialog.form.hint_info" :rows="2" placeholder="不了解选项旁的提示文字" />
        </el-form-item>
        <el-form-item label="自动获取">
          <el-switch v-model="behaviorDialog.form.auto_fetch_enabled" :active-value="1" :inactive-value="0" />
          <span class="form-hint">开启后，评估页会显示"自动获取系统数据"按钮</span>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="behaviorDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="saveBehavior">保存</el-button>
      </span>
    </el-dialog>

    <!-- Excel导入弹窗 -->
    <el-dialog title="Excel批量导入" :visible.sync="importVisible" width="640px" :append-to-body="true">
      <div class="import-area">
        <p class="import-tip">
          <i class="el-icon-info" style="color: var(--color-primary);"></i>
          请上传 .xlsx 或 .xls 文件，需包含以下列：<b>分类,行为描述,分值,案例数,相关性%,历史依据,不了解提示,自动获取(是/否)</b>
        </p>
        <el-upload
          ref="uploadRef"
          action=""
          :auto-upload="false"
          :on-change="handleFileChange"
          :limit="1"
          accept=".xlsx,.xls"
          drag
          class="upload-box"
        >
          <i class="el-icon-upload"></i>
          <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
        </el-upload>
        <div v-if="previewRows.length" class="import-preview">
          <el-divider></el-divider>
          <div class="preview-header">
            <p><b>数据预览：</b>共 {{ previewRows.length }} 行，有效 {{ validPreviewCount }} 行，异常 {{ invalidPreviewCount }} 行</p>
          </div>
          <el-table :data="previewRows" size="small" max-height="300" class="preview-table">
            <el-table-column type="index" label="行号" width="60" />
            <el-table-column prop="category_name" label="分类" width="90" />
            <el-table-column prop="description" label="行为描述" min-width="160" show-overflow-tooltip />
            <el-table-column prop="score" label="分值" width="60" />
            <el-table-column label="状态" width="80">
              <template slot-scope="s">
                <el-tag v-if="s.row.errors.length === 0" size="mini" type="success">正常</el-tag>
                <el-tag v-else size="mini" type="danger">异常</el-tag>
              </template>
            </el-table-column>
          </el-table>
          <div v-if="previewErrors.length" class="preview-errors">
            <p style="color: var(--color-high); font-size: 13px; margin: 8px 0;"><b>异常明细：</b></p>
            <ul>
              <li v-for="(err, idx) in previewErrors" :key="idx">{{ err }}</li>
            </ul>
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="importVisible = false">关闭</el-button>
        <el-button type="primary" :disabled="!canConfirmImport" @click="confirmImport">
          确认导入（{{ validPreviewCount }} 条）
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '../../components/PageHeader.vue'
import * as XLSX from 'xlsx'

export default {
  name: 'BehaviorManage',
  components: { PageHeader },
  data() {
    return {
      // 筛选
      search: '',
      filterCategories: [],
      filterAutoFetch: '',
      filterStatus: '',
      // 分页
      page: 1,
      pageSize: 10,
      // 批量操作
      batchMode: false,
      selectedRows: [],
      // 表格高度（用于表头冻结）
      tableHeight: 'calc(100vh - 300px)',
      // 分类管理弹窗
      categoryDialogVisible: false,
      addingCategory: false,
      newCategoryName: '',
      // 查看弹窗
      viewDialogVisible: false,
      viewRow: null,
      // 行为项弹窗
      behaviorDialog: {
        visible: false,
        isEdit: false,
        form: {
          behavior_id: null,
          category_id: null,
          description: '',
          score: 1,
          evidence_count: 0,
          correlation_rate: 0,
          evidence_desc: '',
          hint_info: '',
          auto_fetch_enabled: 0,
          status: 1
        }
      },
      // Excel导入
      importVisible: false,
      previewRows: [],
      previewErrors: []
    }
  },
  computed: {
    categories() {
      return (this.$store.state.behaviors.categories || []).sort((a, b) => a.sort_order - b.sort_order)
    },
    behaviors() {
      const taskId = this.$store.getters.currentTaskId
      const all = this.$store.state.behaviors.behaviors || []
      if (!taskId) return all
      // 全局行为（无task_id）+ 当前任务的行为
      return all.filter(b => !b.task_id || b.task_id === taskId)
    },
    filteredBehaviors() {
      let data = [...this.behaviors]
      if (this.search) {
        const k = this.search.toLowerCase()
        data = data.filter(b => (b.description || '').toLowerCase().includes(k))
      }
      if (this.filterCategories.length) {
        data = data.filter(b => this.filterCategories.includes(b.category_id))
      }
      if (this.filterAutoFetch !== '') {
        data = data.filter(b => b.auto_fetch_enabled === this.filterAutoFetch)
      }
      if (this.filterStatus !== '') {
        data = data.filter(b => b.status === this.filterStatus)
      }
      return data
    },
    pagedBehaviors() {
      const start = (this.page - 1) * this.pageSize
      return this.filteredBehaviors.slice(start, start + this.pageSize)
    },
    validPreviewCount() {
      return this.previewRows.filter(r => r.errors.length === 0).length
    },
    invalidPreviewCount() {
      return this.previewRows.filter(r => r.errors.length > 0).length
    },
    canConfirmImport() {
      return this.previewRows.length > 0 && this.validPreviewCount > 0
    }
  },
  methods: {
    categoryNameOf(categoryId) {
      const cat = this.categories.find(c => c.category_id === categoryId)
      return cat ? cat.category_name : '-'
    },
    behaviorCountOf(categoryId) {
      return this.behaviors.filter(b => b.category_id === categoryId).length
    },
    // 分页
    handleSizeChange(size) {
      this.pageSize = size
      this.page = 1
    },
    handlePageChange(page) {
      this.page = page
    },
    // 批量操作
    toggleBatchMode() {
      if (this.batchMode) {
        this.exitBatchMode()
      } else {
        this.batchMode = true
        this.selectedRows = []
        this.$nextTick(() => {
          this.$refs.tableRef && this.$refs.tableRef.clearSelection()
        })
      }
    },
    exitBatchMode() {
      this.batchMode = false
      this.selectedRows = []
      this.$nextTick(() => {
        this.$refs.tableRef && this.$refs.tableRef.clearSelection()
      })
    },
    handleSelectionChange(selection) {
      this.selectedRows = selection
    },
    clearSelection() {
      this.selectedRows = []
      this.$refs.tableRef && this.$refs.tableRef.clearSelection()
    },
    async batchEnable() {
      if (!this.selectedRows.length) return
      const ids = this.selectedRows.map(r => r.behavior_id)
      try {
        for (const id of ids) {
          await this.$api.updateBehavior(id, { status: 1 })
        }
        this.$store.commit('BATCH_TOGGLE_BEHAVIOR_STATUS', { ids, status: 1 })
        this.$message.success(`已批量启用 ${ids.length} 项`)
      } catch (e) {
        this.$message.error('批量启用失败: ' + ((e.response && e.response.data && e.response.data.message) || e.message))
      }
      this.clearSelection()
    },
    async batchDisable() {
      if (!this.selectedRows.length) return
      const ids = this.selectedRows.map(r => r.behavior_id)
      try {
        for (const id of ids) {
          await this.$api.updateBehavior(id, { status: 2 })
        }
        this.$store.commit('BATCH_TOGGLE_BEHAVIOR_STATUS', { ids, status: 2 })
        this.$message.success(`已批量禁用 ${ids.length} 项`)
      } catch (e) {
        this.$message.error('批量禁用失败: ' + ((e.response && e.response.data && e.response.data.message) || e.message))
      }
      this.clearSelection()
    },
    async batchChangeCategory(categoryId) {
      if (!this.selectedRows.length) return
      const ids = this.selectedRows.map(r => r.behavior_id)
      try {
        for (const id of ids) {
          await this.$api.updateBehavior(id, { category_id: categoryId })
        }
        this.$store.commit('BATCH_UPDATE_BEHAVIOR_CATEGORY', { ids, category_id: categoryId })
        this.$message.success(`已批量修改 ${ids.length} 项的分类`)
      } catch (e) {
        this.$message.error('批量修改失败: ' + ((e.response && e.response.data && e.response.data.message) || e.message))
      }
      this.clearSelection()
    },
    // 查看弹窗
    openViewDialog(row) {
      this.viewRow = row
      this.viewDialogVisible = true
    },
    // 行为项弹窗
    openBehaviorDialog(row) {
      if (row) {
        this.behaviorDialog.isEdit = true
        this.behaviorDialog.form = { ...row }
      } else {
        this.behaviorDialog.isEdit = false
        this.behaviorDialog.form = {
          behavior_id: null,
          category_id: this.categories[0] ? this.categories[0].category_id : null,
          description: '',
          score: 1,
          evidence_count: 0,
          correlation_rate: 0,
          evidence_desc: '',
          hint_info: '',
          auto_fetch_enabled: 0,
          status: 1
        }
      }
      this.behaviorDialog.visible = true
    },
    async saveBehavior() {
      const form = this.behaviorDialog.form
      if (!form.category_id) {
        this.$message.error('请选择所属分类')
        return
      }
      if (!form.description.trim()) {
        this.$message.error('行为描述不能为空')
        return
      }
      const now = new Date().toISOString().slice(0, 10)
      try {
        if (this.behaviorDialog.isEdit) {
          await this.$api.updateBehavior(form.behavior_id, {
            category_id: form.category_id,
            description: form.description,
            score: form.score,
            evidence_count: form.evidence_count,
            correlation_rate: form.correlation_rate,
            evidence_desc: form.evidence_desc,
            hint_info: form.hint_info,
            auto_fetch_enabled: form.auto_fetch_enabled,
            status: form.status,
            task_id: form.task_id
          })
          this.$store.commit('UPDATE_BEHAVIOR', { ...form, updated_at: now })
          this.$message.success('行为项已更新')
        } else {
          const res = await this.$api.createBehavior({
            category_id: form.category_id,
            description: form.description,
            score: form.score,
            evidence_count: form.evidence_count,
            correlation_rate: form.correlation_rate,
            evidence_desc: form.evidence_desc,
            hint_info: form.hint_info,
            auto_fetch_enabled: form.auto_fetch_enabled,
            status: 1,
            task_id: this.$store.getters.currentTaskId || null
          })
          this.$store.commit('ADD_BEHAVIOR', {
            ...form,
            behavior_id: res.data.behavior_id,
            created_at: now,
            updated_at: now,
            task_id: this.$store.getters.currentTaskId || null
          })
          this.$message.success('行为项已新增')
        }
        this.behaviorDialog.visible = false
      } catch (e) {
        this.$message.error('保存失败: ' + ((e.response && e.response.data && e.response.data.message) || e.message))
      }
    },
    // 分类管理
    moveCategory(index, direction) {
      const list = [...this.categories]
      const target = index + direction
      if (target < 0 || target >= list.length) return
      const temp = list[index]
      list[index] = list[target]
      list[target] = temp
      // 重新分配 sort_order
      list.forEach((c, i) => { c.sort_order = i + 1 })
      this.$store.commit('REORDER_CATEGORIES', list)
    },
    startCategoryEdit(row) {
      this.$set(row, '_editing', true)
      this.$set(row, '_editName', row.category_name)
    },
    async saveCategoryEdit(row) {
      const name = (row._editName || '').trim()
      if (!name) {
        this.$message.error('分类名称不能为空')
        return
      }
      try {
        await this.$api.updateCategory(row.category_id, { category_name: name })
        this.$store.commit('UPDATE_CATEGORY', { ...row, category_name: name })
        row._editing = false
        this.$message.success('分类已更新')
      } catch (e) {
        this.$message.error('更新失败: ' + ((e.response && e.response.data && e.response.data.message) || e.message))
      }
    },
    cancelCategoryEdit(row) {
      row._editing = false
    },
    async confirmAddCategory() {
      const name = this.newCategoryName.trim()
      if (!name) {
        this.$message.error('分类名称不能为空')
        return
      }
      const maxOrder = this.categories.reduce((m, c) => Math.max(m, c.sort_order), 0)
      try {
        const res = await this.$api.createCategory({
          category_name: name,
          sort_order: maxOrder + 1,
          status: 1
        })
        this.$store.commit('ADD_CATEGORY', {
          category_id: res.data.category_id,
          category_name: name,
          sort_order: maxOrder + 1,
          status: 1
        })
        this.newCategoryName = ''
        this.addingCategory = false
        this.$message.success('分类已新增')
      } catch (e) {
        this.$message.error('新增失败: ' + ((e.response && e.response.data && e.response.data.message) || e.message))
      }
    },
    async deleteCategory(cat) {
      const count = this.behaviorCountOf(cat.category_id)
      if (count > 0) {
        this.$message.warning(`该分类下有 ${count} 个行为项，请先移除后再删除分类`)
        return
      }
      try {
        await this.$confirm(`确定删除分类 "${cat.category_name}"？`, '提示', { type: 'warning' })
        await this.$api.deleteCategory(cat.category_id)
        this.$store.commit('DELETE_CATEGORY', cat.category_id)
        this.$message.success('分类已删除')
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error('删除失败: ' + ((e.response && e.response.data && e.response.data.message) || e.message))
        }
      }
    },
    // Excel模板下载
    downloadTemplate() {
      const headers = ['分类', '行为描述', '分值', '案例数', '相关性%', '历史依据', '不了解提示', '自动获取(是/否)']
      const sample = [['工作态度', '投入度明显下降，从主动推进变为被动执行', '2', '86', '72', '基于86个离职案例验证', '观察其项目进度更新频率', '否']]
      const ws = XLSX.utils.aoa_to_sheet([headers, ...sample])
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, '行为项模板')
      XLSX.writeFile(wb, '行为项导入模板.xlsx')
    },
    // Excel导入
    openImport() {
      this.importVisible = true
      this.previewRows = []
      this.previewErrors = []
      this.$nextTick(() => {
        if (this.$refs.uploadRef) this.$refs.uploadRef.clearFiles()
      })
    },
    handleFileChange(file) {
      if (!file.raw) return
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array' })
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 })
          this.buildPreview(jsonData)
        } catch (err) {
          this.$message.error('文件解析失败：' + err.message)
        }
      }
      reader.readAsArrayBuffer(file.raw)
    },
    buildPreview(rows) {
      if (!rows || rows.length < 2) {
        this.$message.warning('文件为空或格式不正确')
        return
      }
      const headers = rows[0].map(h => String(h).trim())
      const catIdx = headers.findIndex(h => h.includes('分类'))
      const descIdx = headers.findIndex(h => h.includes('行为描述'))
      const scoreIdx = headers.findIndex(h => h.includes('分值'))
      const countIdx = headers.findIndex(h => h.includes('案例数'))
      const rateIdx = headers.findIndex(h => h.includes('相关性'))
      const eviIdx = headers.findIndex(h => h.includes('历史依据'))
      const hintIdx = headers.findIndex(h => h.includes('不了解提示'))
      const autoIdx = headers.findIndex(h => h.includes('自动获取'))

      if (catIdx === -1 || descIdx === -1 || scoreIdx === -1) {
        this.$message.error('文件缺少必要的列：分类、行为描述、分值')
        return
      }

      const preview = []
      const errors = []

      for (let i = 1; i < rows.length; i++) {
        const row = rows[i]
        const categoryName = String(row[catIdx] || '').trim()
        const description = String(row[descIdx] || '').trim()
        const scoreVal = Number(row[scoreIdx])

        if (!categoryName && !description) continue

        const rowErrors = []
        if (!categoryName) rowErrors.push('分类不能为空')
        if (!description) rowErrors.push('行为描述不能为空')
        if (isNaN(scoreVal) || scoreVal < 1 || scoreVal > 30) rowErrors.push('分值必须是1-30的数字')

        const autoStr = autoIdx >= 0 ? String(row[autoIdx] || '').trim() : '否'
        const autoEnabled = autoStr === '是' ? 1 : 0

        preview.push({
          _rowIndex: i + 1,
          category_name: categoryName,
          description,
          score: isNaN(scoreVal) ? 1 : scoreVal,
          evidence_count: countIdx >= 0 ? Number(row[countIdx]) || 0 : 0,
          correlation_rate: rateIdx >= 0 ? Number(row[rateIdx]) || 0 : 0,
          evidence_desc: eviIdx >= 0 ? String(row[eviIdx] || '').trim() : '',
          hint_info: hintIdx >= 0 ? String(row[hintIdx] || '').trim() : '',
          auto_fetch_enabled: autoEnabled,
          errors: rowErrors
        })

        rowErrors.forEach(err => {
          errors.push(`第${i + 1}行（${description || categoryName}）：${err}`)
        })
      }

      this.previewRows = preview
      this.previewErrors = errors
      this.$message.info(`解析完成：共 ${preview.length} 行，有效 ${this.validPreviewCount} 行`)
    },
    confirmImport() {
      if (!this.previewRows.length) return
      let success = 0
      for (const row of this.previewRows) {
        if (row.errors.length > 0) continue

        // 查找或创建分类
        let category = this.categories.find(c => c.category_name === row.category_name)
        if (!category) {
          const maxOrder = this.categories.reduce((m, c) => Math.max(m, c.sort_order), 0)
          category = {
            category_id: Date.now() + Math.floor(Math.random() * 1000),
            category_name: row.category_name,
            sort_order: maxOrder + 1,
            status: 1
          }
          this.$store.commit('ADD_CATEGORY', category)
        }

        const id = 200 + this.behaviors.length + Date.now() + Math.floor(Math.random() * 1000)
        const now = new Date().toISOString().slice(0, 10)
        this.$store.commit('ADD_BEHAVIOR', {
          behavior_id: id,
          category_id: category.category_id,
          description: row.description,
          score: row.score,
          evidence_count: row.evidence_count,
          correlation_rate: row.correlation_rate,
          evidence_desc: row.evidence_desc,
          hint_info: row.hint_info,
          auto_fetch_enabled: row.auto_fetch_enabled,
          status: 1,
          created_at: now,
          updated_at: now,
          task_id: this.$store.getters.currentTaskId || null
        })
        success++
      }
      this.$message.success(`导入完成：成功 ${success} 条`)
      this.previewRows = []
      this.$nextTick(() => {
        if (this.$refs.uploadRef) this.$refs.uploadRef.clearFiles()
      })
    }
  }
}
</script>

<style scoped>
.toolbar { display: flex; align-items: center; margin-bottom: var(--space-lg); }
.filter-item { display: flex; align-items: center; gap: 6px; background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-md); padding: 0 8px; }
.filter-label { font-size: 12px; color: var(--color-text-secondary); white-space: nowrap; }
.table-wrapper { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-lg); overflow: hidden; box-shadow: var(--shadow-sm); }
.data-table >>> th { background: var(--color-bg) !important; color: var(--color-text) !important; font-weight: 600 !important; }

/* 批量操作栏（表格上方） */
.batch-bar-top { display: flex; align-items: center; justify-content: space-between; padding: var(--space-md) var(--space-lg); background: var(--color-bg); border: 1px solid var(--color-border); border-bottom: none; border-radius: var(--radius-lg) var(--radius-lg) 0 0; margin-bottom: 0; }
.batch-info { font-size: 13px; color: var(--color-text-secondary); }
.batch-actions { display: flex; gap: var(--space-sm); }

/* 单元格样式 */
.category-tag { font-size: 12px; color: var(--color-text); }

/* 分页 */
.pagination-bar { display: flex; justify-content: flex-end; padding: var(--space-md) var(--space-lg); background: var(--color-surface); border-top: 1px solid var(--color-border); }

/* 分类管理弹窗 */
.category-manage-table >>> th { background: var(--color-bg) !important; color: var(--color-text) !important; font-weight: 600 !important; }

/* 查看弹窗 */
.view-dialog-body { padding: 0 10px; }
.view-row { margin-bottom: 14px; display: flex; align-items: flex-start; }
.view-label { font-size: 13px; font-weight: 600; color: var(--color-text-secondary); white-space: nowrap; min-width: 90px; }
.view-text { margin: 0; font-size: 14px; color: var(--color-text); line-height: 1.6; flex: 1; }

/* 导入 */
.import-area { padding: 0 10px; }
.import-tip { font-size: 13px; color: var(--color-text-secondary); margin-bottom: 16px; line-height: 1.6; }
.upload-box >>> .el-upload { width: 100%; }
.upload-box >>> .el-upload-dragger { width: 100%; }
.preview-header { margin-bottom: 10px; }
.preview-header p { font-size: 13px; color: var(--color-text-secondary); margin: 0 0 8px 0; }
.preview-errors ul { max-height: 120px; overflow-y: auto; margin: 8px 0; padding-left: 20px; color: var(--color-high); font-size: 13px; }
.preview-errors li { margin-bottom: 4px; }

/* 表单提示 */
.form-hint { margin-left: 10px; font-size: 12px; color: var(--color-text-muted); }

.action-edit { color: var(--color-primary) !important; }
</style>
